import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUp() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", second_name: "", email: "", phone: "",
    password: "", verify_password: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState("");   // success message from server

  const upd = (k) => (e) => {
    let v = e.target.value;
    if (k === "phone") v = v.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    const { first_name, second_name, email, phone, password, verify_password } = form;
    if (!first_name.trim() || !second_name.trim()) return toast.error("Enter both first and second name");
    if (!EMAIL_RE.test(email.trim())) return toast.error("Enter a valid email");
    if (!/^\d{10}$/.test(phone)) return toast.error("Phone must be 10 digits");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== verify_password) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setDone(data.message || "Sign-up submitted");
      toast.success("Request submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <Card className="max-w-lg w-full p-10 text-center border-gray-200 shadow-sm">
          <CheckCircle2 className="h-14 w-14 mx-auto text-green-600" />
          <h2 className="font-display text-2xl font-bold mt-4">Request received</h2>
          <p className="text-gray-600 mt-4 leading-relaxed" data-testid="signup-success-message">{done}</p>
          <Button data-testid="signup-back-to-login-btn" className="mt-8 bg-brand-red hover:bg-[#B31721]" onClick={() => navigate("/login")}>
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="header-dark md:w-1/2 p-8 md:p-14 flex flex-col justify-between">
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 shrink-0">
            <img src="/logo.png" alt="Coke @9" className="h-full w-full object-contain" />
          </div>
          <div className="font-display font-black text-white leading-none tracking-tight text-5xl sm:text-6xl md:text-7xl whitespace-nowrap">
            Coke <span className="text-brand-red">@9/-</span>
          </div>
        </div>

        <div className="my-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Request Access</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white mt-3 leading-tight">
            Join the<br/>
            <span className="text-brand-red">Control Tower.</span>
          </h1>
          <p className="mt-5 text-neutral-400 text-base max-w-md leading-relaxed">
            After sign-up, the Admin will assign your role, project and store. You'll be able to log in as soon as your request is approved.
          </p>
        </div>

        <Link to="/login" className="text-neutral-400 text-xs hover:text-white flex items-center gap-2" data-testid="signup-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to login
        </Link>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-6 md:p-14 bg-gray-50">
        <Card className="w-full max-w-md p-8 rounded-2xl border-gray-200 shadow-sm bg-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Sign up</div>
          <h2 className="font-display text-2xl font-bold text-gray-900 mt-1">Request a login</h2>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700">First Name*</label>
                <Input data-testid="signup-first-name-input" className="h-11 mt-1"
                  value={form.first_name} onChange={upd("first_name")} placeholder="First name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Second Name*</label>
                <Input data-testid="signup-second-name-input" className="h-11 mt-1"
                  value={form.second_name} onChange={upd("second_name")} placeholder="Last name" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">Email ID*</label>
              <Input data-testid="signup-email-input" type="email" className="h-11 mt-1"
                value={form.email} onChange={upd("email")} placeholder="you@example.com" />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">Phone No.*</label>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-11 px-3 flex items-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</div>
                <Input data-testid="signup-phone-input" inputMode="numeric" maxLength={10} className="h-11"
                  value={form.phone} onChange={upd("phone")} placeholder="10-digit mobile" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">Create Password*</label>
              <div className="mt-1 relative">
                <Input data-testid="signup-password-input" type={showPw ? "text" : "password"} className="h-11 pr-11"
                  value={form.password} onChange={upd("password")} placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  data-testid="signup-toggle-password">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">Verify Password*</label>
              <Input data-testid="signup-verify-password-input" type={showPw ? "text" : "password"} className="h-11 mt-1"
                value={form.verify_password} onChange={upd("verify_password")} placeholder="Retype your password" />
            </div>

            <Button data-testid="signup-submit-btn" type="submit" disabled={loading}
              className="w-full h-12 bg-brand-red hover:bg-[#B31721] text-white rounded-xl tap-btn font-semibold">
              {loading ? "Submitting…" : "Submit"}
            </Button>

            <p className="text-[11px] text-gray-500 leading-relaxed">
              We never share your details. Your password is stored one-way encrypted and cannot be seen by anyone — not even the Admin. Admin can only reset it.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
